import _memoize from 'lodash/memoize';

// Parts of this code are originally from: https://github.com/google/site-kit-wp/blob/a2cabed123c702969fffad9ed2e122f7d9edc89c/assets/js/util/i18n.js
// which is under Apache 2 so the original license is included here

/**
 * Internationalization Utilities.
 *
 * Site Kit by Google, Copyright 2021 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// Warn once for a given message.
const warnOnce = _memoize(console.warn); // eslint-disable-line no-console

/**
 * Returns an Intl.NumberFormat instance that can be used to format a number.
 * This function exists because there are bugs caused by using some features of the
 * Intl.NumberFormat API in certain browsers, such as Safari v14.1. Rather than allow those bugs to
 * crash the app, this function will remove the option parameters that cause the problem, allowing
 * a graceful fallback.

 * @param locale the locale, such as 'en'
 * @param options - see the NumberFormat {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/NumberFormat/NumberFormat|`options` docs}
 * @returns the formatted number.
 */
export const getNumberFormat = (
    locale: string,
    formatOptions = {} as Intl.NumberFormatOptions,
): Intl.NumberFormat => {
    try {
        /**
         * If an error is thrown, we remove some troublesome params from the formatOptions object
         * and fallback to no formatting.
         *
         * This allows us to degrade somewhat gracefully without breaking the dashboard for users of
         * unaffected browsers.
         */
        return new Intl.NumberFormat(locale, formatOptions);
    } catch (error) {
        let message = 'Caught error during new Int.NumberFormat()';

        if (error instanceof Error) {
            ({ message } = error);
        }

        warnOnce(
            `numberFormat error: Intl.NumberFormat( ${JSON.stringify(
                locale,
            )}, ${JSON.stringify(formatOptions)} ).format)`,
            message,
        );
    }

    // Remove these key/values from formatOptions.
    const unstableFormatOptionValues: Record<string, string> = {
        currencyDisplay: 'narrow',
        currencySign: 'accounting',
        style: 'unit',
    };

    // Remove these keys from formatOptions irrespective of value.
    const unstableFormatOptions = ['signDisplay', 'compactDisplay'];

    const reducedFormatOptions: Record<string, string> = {};

    for (const [key, value] of Object.entries(formatOptions)) {
        if (
            unstableFormatOptionValues[key] &&
            value === unstableFormatOptionValues[key]
        ) {
            // eslint-disable-next-line no-continue
            continue;
        }

        if (unstableFormatOptions.includes(key)) {
            // eslint-disable-next-line no-continue
            continue;
        }

        reducedFormatOptions[key] = value;
    }

    try {
        return new Intl.NumberFormat(locale, reducedFormatOptions);
    } catch {
        return new Intl.NumberFormat(locale);
    }
};
